import React, { useEffect, useState, useCallback } from "react"
import { Icon } from "~/react/ui/icon"
import { Skeleton } from "~/react/ui/skeleton"
import { Card, CardTitle, CardTitleActions, CardContent } from "~/react/ui/card"
import { Gauge } from "~/react/ui/health-gauge"
import { Separator } from "~/react/ui/separator"
import { Hint } from "~/react/ui/hint"
import EmailCapacityBreakdown from "~/react/components/email_accounts/email_capacity_breakdown"
import EmailAccountSevenDayChart from "~/react/components/email_accounts/charts/email_account_seven_day_chart"
import IssuesFeed from "~/react/components/issues-feed"
import { useEmailAccountUsage } from "~/react/hooks/useEmailAccountUsage"
import {
  deliverability_guard_path,
  usage_email_accounts_path,
  email_domains_health_reports_path,
} from "~/lib/routes"

interface GlobalHealthData {
  score: number | null
  status: string | null
}

interface SendingActivityData {
  sent_today: number
  scheduled: number
  available: number
  daily_limit: number
  sent_last_7_days: Array<{ date: string; count: number; capacity: number }>
}

interface DeliverabilityGuardIssue {
  severity: "critical" | "high" | "medium" | "low"
  description: string
  category: string
  domain?: string
  email_account?: string
}

const getHealthStatusLabel = (status: string | null): string => {
  if (!status) return ""
  return i18n.t(`js.components.health_gauge.${status}.title`)
}

const getHealthDescription = (
  status: string | null,
  score: number | null,
): string => {
  if (score === null)
    return i18n.t("js.email_accounts_center.global_health.description.no_data")
  if (!status) return ""
  return i18n.t(`js.components.health_gauge.${status}.description`)
}

const GlobalHealthWidget: React.FC<{
  data: GlobalHealthData | null
  loading: boolean
}> = ({ data, loading }) => {
  if (loading) {
    return (
      <Card variant="widget" className="widget-card--square">
        <header className="widget-card__header">
          <CardTitle>
            {i18n.t("js.campaigns.email_accounts_center.global_health.title")}
            <CardTitleActions>
              <button
                className="question-circle"
                data-controller="tooltip"
                title={i18n.t(
                  "js.campaigns.email_accounts_center.global_health.tooltip",
                )}
              >
                <Icon icon="question-circle" size="sm" />
              </button>
            </CardTitleActions>
          </CardTitle>
        </header>
        <CardContent>
          <Skeleton
            size="freeform"
            className="my-5"
            style={{
              width: "100%",
              height: "100px",
              borderRadius: "100px 100px 0 0",
            }}
          />
          <Separator orientation="horizontal" className="my-3" />
          <Skeleton
            size="freeform"
            style={{ width: "100%", height: "16px", borderRadius: "4px" }}
          />
        </CardContent>
      </Card>
    )
  }

  const score = data?.score ?? null
  const status = data?.status ?? null
  const statusLabel = getHealthStatusLabel(status)
  const description = getHealthDescription(status, score)

  return (
    <Card variant="widget" className="widget-card--square">
      <header className="widget-card__header">
        <CardTitle>
          {i18n.t("js.campaigns.email_accounts_center.global_health.title")}
          <CardTitleActions>
            <button
              className="question-circle"
              data-controller="tooltip"
              title={i18n.t(
                "js.campaigns.email_accounts_center.global_health.tooltip",
              )}
            >
              <Icon icon="question-circle" size="sm" />
            </button>
          </CardTitleActions>
        </CardTitle>
      </header>
      <CardContent>
        <Gauge
          value={score ?? 0}
          max={100}
          valueSuffix={<span className="text-sm-medium text-hint">/ 100</span>}
        >
          <div className="text-sm-medium text-center mt-2">{statusLabel}</div>
        </Gauge>
        <Separator orientation="horizontal" className="my-3" />
        <div className="text-center">
          <Hint>{description}</Hint>
        </div>
      </CardContent>
    </Card>
  )
}

const SendingActivityWidget: React.FC<{
  data: SendingActivityData | null
  loading: boolean
}> = ({ data, loading }) => {
  if (loading) {
    return (
      <Card variant="widget">
        <header className="widget-card__header">
          <CardTitle>
            {i18n.t(
              "js.campaigns.email_accounts_center.sending_activity.title",
            )}
            <CardTitleActions>
              <button
                className="question-circle"
                data-controller="tooltip"
                title={i18n.t(
                  "js.campaigns.email_accounts_center.sending_activity.tooltip",
                )}
              >
                <Icon icon="question-circle" size="sm" />
              </button>
            </CardTitleActions>
          </CardTitle>
        </header>
        <CardContent>
          <div className="mb-4">
            <Skeleton
              size="freeform"
              style={{ width: "100%", height: "0.6rem", borderRadius: "4px" }}
              className="mb-2"
            />
            <div className="d-flex gap-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton
                  key={i}
                  size="freeform"
                  style={{ width: "80px", height: "40px", borderRadius: "4px" }}
                />
              ))}
            </div>
          </div>
          <div>
            <Skeleton
              size="freeform"
              style={{ width: "100%", height: "140px", borderRadius: "4px" }}
            />
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!data) {
    return null
  }

  const { sent_today, scheduled, daily_limit, sent_last_7_days } = data

  return (
    <Card variant="widget">
      <header className="widget-card__header">
        <CardTitle>
          {i18n.t("js.campaigns.email_accounts_center.sending_activity.title")}
          <CardTitleActions>
            <button
              className="question-circle"
              data-controller="tooltip"
              title={i18n.t(
                "js.campaigns.email_accounts_center.sending_activity.tooltip",
              )}
            >
              <Icon icon="question-circle" size="sm" />
            </button>
          </CardTitleActions>
        </CardTitle>
      </header>
      <CardContent>
        <strong className="text-xs-medium">
          {i18n.t(
            "js.campaigns.email_accounts_center.sending_activity.today_usage",
          )}
        </strong>
        <EmailCapacityBreakdown
          sent={sent_today}
          scheduled={scheduled}
          limit={daily_limit}
          showLegend={true}
          chartSize="lg"
          className="mt-2 mb-4"
        />

        <strong className="text-xs-medium">
          {i18n.t(
            "js.campaigns.email_accounts_center.sending_activity.seven_day_history",
          )}
        </strong>
        <EmailAccountSevenDayChart
          data={sent_last_7_days}
          dailyLimit={daily_limit}
        />
      </CardContent>
    </Card>
  )
}

const EmailAccountsWidgets: React.FC = () => {
  const healthEndpoint = email_domains_health_reports_path({ format: "json" })
  const usageEndpoint = usage_email_accounts_path({ format: "json" })
  const issuesEndpoint = deliverability_guard_path({ format: "json" })
  const [healthData, setHealthData] = useState<GlobalHealthData | null>(null)
  const [healthLoading, setHealthLoading] = useState(true)
  const [usageData, setUsageData] = useState<SendingActivityData | null>(null)
  const [usageLoading, setUsageLoading] = useState(true)
  const [issues, setIssues] = useState<DeliverabilityGuardIssue[]>([])
  const [issuesLoading, setIssuesLoading] = useState(true)

  const fetchHealth = useCallback(() => {
    fetch(healthEndpoint, {
      headers: {
        Accept: "application/json",
      },
      credentials: "same-origin",
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP error ${res.status}`)
        return res.json()
      })
      .then(setHealthData)
      .catch((error) => {
        console.error("Failed to fetch global health:", error)
      })
      .finally(() => setHealthLoading(false))
  }, [healthEndpoint])

  const fetchUsage = useCallback(() => {
    fetch(usageEndpoint, {
      headers: {
        Accept: "application/json",
      },
      credentials: "same-origin",
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP error ${res.status}`)
        return res.json()
      })
      .then(setUsageData)
      .catch((error) => {
        console.error("Failed to fetch sending activity:", error)
      })
      .finally(() => setUsageLoading(false))
  }, [usageEndpoint])

  // Real-time updates via ActionCable when any account's usage changes
  const handleUsageUpdate = useCallback(() => {
    // Refetch aggregate usage for all accounts
    fetchUsage()
  }, [fetchUsage])

  useEmailAccountUsage({ onUpdate: handleUsageUpdate })

  useEffect(() => {
    const fetchIssues = () => {
      fetch(issuesEndpoint, {
        headers: {
          Accept: "application/json",
        },
        credentials: "same-origin",
      })
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP error ${res.status}`)
          return res.json()
        })
        .then(setIssues)
        .catch((error) => {
          console.error("Failed to fetch deliverability guard issues:", error)
        })
        .finally(() => setIssuesLoading(false))
    }

    fetchHealth()
    fetchUsage()
    fetchIssues()
  }, [fetchHealth, fetchUsage, issuesEndpoint])

  return (
    <div className="eac-widgets mb-8">
      <GlobalHealthWidget data={healthData} loading={healthLoading} />
      <SendingActivityWidget data={usageData} loading={usageLoading} />
      <IssuesFeed
        issues={issues}
        loading={issuesLoading}
        title={i18n.t(
          "js.campaigns.email_accounts_center.deliverability_guard.title",
        )}
        tooltip={i18n.t(
          "js.campaigns.email_accounts_center.deliverability_guard.tooltip",
        )}
        emptyStateTitle={i18n.t(
          "js.campaigns.email_accounts_center.deliverability_guard.empty_state.title",
        )}
        emptyStateDescription={i18n.t(
          "js.campaigns.email_accounts_center.deliverability_guard.empty_state.description",
        )}
        getters={{
          title: (issue) => {
            if (issue.email_account) {
              return `${i18n.t("js.campaigns.email_accounts_center.deliverability_guard.scope.email_account")} / ${issue.email_account}`
            } else if (issue.domain) {
              return `${i18n.t("js.campaigns.email_accounts_center.deliverability_guard.scope.domain")} / ${issue.domain}`
            }
            return issue.description
          },
          key: (issue, index) =>
            `${issue.domain || issue.email_account || "unknown"}-${issue.category}-${index}`,
          action: () => null,
        }}
      />
    </div>
  )
}

export default EmailAccountsWidgets
